import React, { useContext, useMemo } from 'react';
import GenericNavbarLayout from '../GenericNavbarLayout';
import { NavLinkData } from '../../util/navLinkData';
import { RouteLayoutProps } from '../routeLayoutProps';
import AdminDashboard from '@views/admin/AdminDashboard';
import { StaffRoutingContext } from './staffRoutingContext';
import CourseReview from '@views/admin/course/CourseReview';
import { AdminAuthContext, DepartmentChairAuthContext, IdTokenWithClaims } from '@src/authContexts';
import { AdminSubRole } from '@ypp-portal/models/user/role';
import SendMassUpdate from '@views/admin/SendMassUpdate';
import CourseReviewPage from '@views/admin/course/CourseReviewPage';
import PersonnelRecords from '@views/admin/records/PersonnelRecords';
import AdminAccount from '@views/admin/auth/AdminAccount';
import StudentRecords from '@views/admin/records/StudentRecords';
import { DepartmentChairClaimsData } from '@ypp-portal/models/user/staffData';
import CurriculumReview from '@views/admin/course/CurriculumReview';
import CurriculumReviewPage from '@views/admin/course/CurriculumReviewPage';

export type StaffNavLinkData = NavLinkData & {
  inResources: boolean;
};

const genericStaffRoutes: StaffNavLinkData[] = [
  {
    text: 'Home',
    to: '/',
    component: AdminDashboard,
    inNavbar: true,
    inResources: false,
  },
  {
    text: 'Account',
    to: '/account',
    component: AdminAccount,
    inNavbar: true,
    inResources: false,
  },
];

const getRoutes = (subrole: AdminSubRole): StaffNavLinkData[] => {
  const routes: StaffNavLinkData[] = [];

  const addIfHasOne = (route: StaffNavLinkData, roles: AdminSubRole[]) => {
    if (roles.includes(subrole)) {
      routes.push(route);
    }
  };

  addIfHasOne(
    {
      text: 'Send Mass Update',
      to: '/admin/sendupdate',
      component: SendMassUpdate,
      inNavbar: false,
      inResources: true,
    },
    [AdminSubRole.President, AdminSubRole.ChiefOfStaff, AdminSubRole.CAO, AdminSubRole.DirectorOfSE]
  );

  addIfHasOne(
    {
      text: 'Course Review',
      to: '/admin/courses/review',
      component: CourseReview,
      inNavbar: false,
      inResources: true,
    },
    [AdminSubRole.President, AdminSubRole.ChiefOfStaff, AdminSubRole.CAO, AdminSubRole.COO]
  );

  addIfHasOne(
    {
      to: '/admin/courses/review/:field',
      component: CourseReviewPage,
      inNavbar: false,
      inResources: false,
    },
    [AdminSubRole.President, AdminSubRole.ChiefOfStaff, AdminSubRole.CAO, AdminSubRole.COO]
  );

  addIfHasOne(
    {
      text: 'Curriculum Review',
      to: '/admin/curriculum/review',
      component: CurriculumReview,
      inNavbar: false,
      inResources: true,
    },
    [AdminSubRole.DepartmentChair, AdminSubRole.DepartmentSubChair]
  );

  addIfHasOne(
    {
      to: '/admin/curriculum/review/:field',
      component: CurriculumReviewPage,
      inNavbar: false,
      inResources: false,
    },
    [AdminSubRole.DepartmentChair, AdminSubRole.DepartmentSubChair]
  );

  addIfHasOne(
    {
      text: 'Personnel Records',
      to: '/admin/records/personnel',
      component: PersonnelRecords,
      inNavbar: false,
      inResources: true,
    },
    [
      AdminSubRole.President,
      AdminSubRole.ChiefOfStaff,
      AdminSubRole.CFO,
      AdminSubRole.COO,
      AdminSubRole.CAO,
      AdminSubRole.Tech,
    ]
  );

  addIfHasOne(
    {
      text: 'Student Records',
      to: '/records/students',
      component: StudentRecords,
      inNavbar: false,
      inResources: true,
    },
    [
      AdminSubRole.President,
      AdminSubRole.ChiefOfStaff,
      AdminSubRole.CAO,
      AdminSubRole.DirectorOfSE,
      AdminSubRole.CFO,
      AdminSubRole.Tech,
    ]
  );

  return routes;
};

const StaffLayout: React.FC<RouteLayoutProps> = ({ baseRoutes }) => {
  const { idToken, ...rest } = useContext(AdminAuthContext);

  const computedRoutes = useMemo(() => getRoutes(idToken.claims.subrole), [idToken.claims.subrole]);
  const allStaffRoutes = genericStaffRoutes.concat(computedRoutes);

  const allowedRoutes = (allStaffRoutes as NavLinkData[]).concat(baseRoutes);

  if (
    idToken.claims.subrole === AdminSubRole.DepartmentChair ||
    idToken.claims.subrole === AdminSubRole.DepartmentSubChair
  ) {
    const chairToken = idToken as IdTokenWithClaims<DepartmentChairClaimsData>;

    return (
      <StaffRoutingContext.Provider value={{ routes: allStaffRoutes }}>
        <DepartmentChairAuthContext.Provider value={{ idToken: chairToken, ...rest }}>
          <GenericNavbarLayout allowedRoutes={allowedRoutes}></GenericNavbarLayout>
        </DepartmentChairAuthContext.Provider>
      </StaffRoutingContext.Provider>
    );
  }

  return (
    <StaffRoutingContext.Provider value={{ routes: allStaffRoutes }}>
      <GenericNavbarLayout allowedRoutes={allowedRoutes}></GenericNavbarLayout>
    </StaffRoutingContext.Provider>
  );
};

export default StaffLayout;
